/**
 * Human-readable rows for a proposal change: label, before → after.
 * Pure; the caller passes the translator scoped to agents.builder.
 */

import type { AgentBuilderChange } from '@/lib/api';

type T = (key: string, values?: Record<string, unknown>) => string;

export interface DiffRow {
  key: string;
  label: string;
  /** undefined = no "before" column (create). */
  before?: string;
  after: string;
  /** Multi-line or long text: render as blocks instead of inline. */
  long: boolean;
  /** Voice id when the row is a voice choice (▶ preview). */
  voice?: string;
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

const LONG_TEXT = 90;

function formatValue(key: string, value: unknown, t: T): string {
  if (value === undefined || value === null || value === '') {
    return key.endsWith('quietHours') && value === null ? t('value.quietOff') : t('value.empty');
  }
  if (typeof value === 'boolean') return value ? t('value.on') : t('value.off');
  if (key === 'toolsScope' && isObject(value)) {
    const entries = Object.entries(value);
    if (!entries.length) return t('value.noData');
    return entries
      .map(([entity, ops]) => `${entity}: ${(Array.isArray(ops) ? ops : []).map((op) => t(`ops.${op}`, { defaultValue: String(op) })).join(', ')}`)
      .join('\n');
  }
  if (key === 'rules' && Array.isArray(value)) {
    if (!value.length) return t('value.noRules');
    return value
      .map((r) => {
        const rule = isObject(r) ? r : {};
        const match = Array.isArray(rule.match) ? rule.match.join(' + ') : String(rule.match ?? '');
        return `“${match}” → ${String(rule.response ?? '')}`;
      })
      .join('\n');
  }
  if (key.endsWith('quietHours') && isObject(value)) return `${value.start}–${value.end}`;
  if (key === 'locale') return value === 'en' ? t('value.localeEn') : t('value.localeEs');
  if (key === 'kind') return t(`kind.${String(value)}`, { defaultValue: String(value) });
  if (key === 'delivery' && isObject(value)) return `Slack · ${String(value.target ?? '')}`;
  if (Array.isArray(value)) return value.map((v, i) => `${i + 1}. ${String(v)}`).join('\n');
  if (isObject(value)) return JSON.stringify(value);
  return String(value);
}

function row(key: string, label: string, after: unknown, t: T, before?: { value: unknown }): DiffRow {
  const a = formatValue(key, after, t);
  const b = before ? formatValue(key, before.value, t) : undefined;
  const long = [a, b ?? ''].some((s) => s.length > LONG_TEXT || s.includes('\n'));
  return {
    key,
    label,
    after: a,
    ...(b !== undefined ? { before: b } : {}),
    long,
    ...(key === 'voice.voice' && typeof after === 'string' ? { voice: after } : {}),
  };
}

const label = (t: T, key: string) => t(`fields.${key.replace(/\./g, '_')}`, { defaultValue: key });

/** Flatten settings one level (voice.*, autonomy.*). */
function settingsRows(after: Record<string, unknown>, before: Record<string, unknown> | undefined, t: T): DiffRow[] {
  const rows: DiffRow[] = [];
  for (const [k, v] of Object.entries(after)) {
    if (isObject(v) && (k === 'voice' || k === 'autonomy')) {
      const b = isObject(before?.[k]) ? (before![k] as Record<string, unknown>) : undefined;
      for (const [sub, sv] of Object.entries(v)) {
        const key = `${k}.${sub}`;
        rows.push(row(key, label(t, key), sv, t, before ? { value: b?.[sub] } : undefined));
      }
    } else {
      rows.push(row(k, label(t, k), v, t, before ? { value: before[k] } : undefined));
    }
  }
  return rows;
}

/** Name shown next to the part (ritual/playbook/agent name). */
export function changeSubject(change: AgentBuilderChange): string | null {
  const after = isObject(change.after) ? change.after : {};
  const before = isObject(change.before) ? change.before : {};
  if (change.target === 'schedule' || change.target === 'create_agent') return String(after.name ?? before.name ?? '') || null;
  if (change.target === 'playbook') return String(after.name ?? before.name ?? change.playbook_key ?? '') || null;
  return null;
}

export function describeChange(change: AgentBuilderChange, t: T): DiffRow[] {
  const after = isObject(change.after) ? change.after : {};
  const before = isObject(change.before) ? change.before : undefined;
  const isCreate = change.operation === 'create';

  if (change.operation === 'delete') {
    if (change.target === 'schedule') {
      return [row('when', label(t, 'when'), change.preview?.before_rrule_text ?? before?.rrule ?? before?.run_at, t)];
    }
    return [];
  }

  switch (change.target) {
    case 'settings':
      return settingsRows(after, isCreate ? undefined : before ?? {}, t);
    case 'schedule': {
      const rows: DiffRow[] = [];
      const when = change.preview?.rrule_text ?? (after.rrule as string | undefined) ?? (after.runAt as string | undefined);
      const beforeWhen = change.preview?.before_rrule_text ?? (before?.rrule as string | undefined);
      if (when !== undefined && (isCreate || 'rrule' in after || 'runAt' in after)) {
        rows.push(row('when', label(t, 'when'), when, t, isCreate ? undefined : { value: beforeWhen }));
      }
      for (const k of ['name', 'kind', 'prompt', 'timezone', 'enabled', 'delivery']) {
        if (k in after && !(isCreate && k === 'name')) {
          rows.push(row(k, label(t, k), after[k], t, isCreate ? undefined : { value: before?.[k] }));
        }
      }
      return rows;
    }
    case 'create_agent': {
      const rows: DiffRow[] = [];
      for (const [k, v] of Object.entries(after)) {
        if (k === 'name') continue;
        if (k === 'settings' && isObject(v)) rows.push(...settingsRows(v, undefined, t));
        else rows.push(row(k, label(t, k), v, t));
      }
      return rows;
    }
    default:
      return Object.entries(after).map(([k, v]) =>
        row(k, label(t, k), v, t, isCreate ? undefined : { value: before?.[k] }),
      );
  }
}

/** "vie 2 oct, 17:00" in the schedule timezone. */
export function formatRun(iso: string, timezone: string | undefined, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale === 'es' ? 'es-AR' : 'en-US', {
      timeZone: timezone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(date);
  } catch {
    return iso;
  }
}
